import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { format, parse, isValid } from 'date-fns';
import toast from 'react-hot-toast';
import api from 'state/axios';
import { useTheme, useMediaQuery, Box,  
  CircularProgress, Typography, Tooltip, IconButton, 
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { 
  Clear,
  SyncAlt,
  Refresh,
  Save,
  AddCircleOutline, 
  Edit,
  Update,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import DataGridComponent from 'components/DataGridComponent';
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { de, enGB } from 'date-fns/locale';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { getSelectedIds,
  isAnyRowSelected,
  parseUTCDate
} from 'state/helpers';
import { hasPermission } from 'state/checkPermission';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';


const FixedBlockTimes = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const locales = { 'en-us': undefined, 'en-gb': enGB, de };
  const user = useSelector((state) => state.user); 
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));  

  const canCreate = hasPermission(user?.permissions, "FixedBlockTimes", "create", false);
  const canEdit = hasPermission(user?.permissions, "FixedBlockTimes", "update", false);
  const canDelete = hasPermission(user?.permissions, "FixedBlockTimes", "delete", false);

  const { startDate, endDate } = useDateRangeContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);  
 
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });
  const [acTypes, setACTypes] = useState([]);
  const [fixedBlockTimes, setFixedBlockTimes] = useState([]);
  const [selectedACTypes, setSelectedACTypes] = useState([]);
  const [untilDate, setUntilDate] = useState(null);



  const fetchACTypes = useCallback(async () => {
    return await api.get('/actypes/active');
  }, []);
  
  const fetchFixedBlockTimes = useCallback(async () => {
    if (!startDate || !endDate) return [];
    const url = '/fixedblocktimes';
    const payload = { startDate, endDate };
                
    return await api.post(url, payload);
  }, [startDate, endDate]);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        setLoading(true);
        
        const [
          actypesResponse,
          fixedBlockTimesResponse, 
        ] = await Promise.all([
          fetchACTypes(),
          fetchFixedBlockTimes(),
        ]);
        
        setACTypes(actypesResponse.data);                
        setFixedBlockTimes(fixedBlockTimesResponse.data);                         
      } catch (error) {
        setError(error);
        toast.error(error.response?.data?.message || "Failed to fetch data!");
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();      
  }, [
    fetchACTypes, 
    fetchFixedBlockTimes, 
  ]); 

  const handleFixedBlockTimes = () => {
    fetchFixedBlockTimes().then(response => {
      setFixedBlockTimes(response.data);
    }).catch(error => {
      toast.error(error.response?.data?.message || "Failed to fetch fixed block times!");
    });    
  }

  const cleanDate = (dateString) => {
    try {
      if (!dateString) return null;
    
      const cleaned = dateString.replace(/[\n\r\t]/g, "").trim();

      // Nếu là ISO string thì dùng new Date()
      const isoDate = new Date(cleaned);
      if (isValid(isoDate)) {
        return format(isoDate, "yyyy-MM-dd");
      }

      // Nếu không phải ISO, thử theo các format khác
      const formatsToTry = [
        "yyyy-MM-dd",
        "yyyy/MM/dd",
        "dd/MM/yyyy",
        "MM/dd/yyyy",
        "dd-MM-yyyy",
      ];

      for (const fmt of formatsToTry) {
        const parsed = parse(cleaned, fmt, new Date());
        if (isValid(parsed)) {
          return format(parsed, "yyyy-MM-dd");
        }
      }

      return null;
    } catch {
      return null;
    }
  };

  const parseExcelData = (data) => {
    try {
      const rows = data
        .trim()
        .split("\n")
        .map((row, index) => {
          const cells = row.split("\t");
          if (cells.length < 4 || cells.length > 5) {
            throw new Error(`Row ${index + 1} is invalid or incomplete.`);
          }

          return {
            _id: index + 1, 
            sector: cells[0],
            acTypes: cells[1],
            blockTime: cells[2],
            validSince: cleanDate(cells[3]),
            validUntil: cleanDate(cells[4]),
          };
        });

      return rows;
    } catch (error) {
      toast.success(error?.message || "Failed to parse the clipboard!");
      return null;
    }
  };

  const handlePaste = (event) => {
    try
    {
      event.preventDefault();
      const clipboardData = event.clipboardData.getData("text");
      const parsedRows = parseExcelData(clipboardData);
  
      if (parsedRows) {
        setFixedBlockTimes((prevRows) => [...prevRows, ...parsedRows]);
        setRowSelectionModel({ type: 'include', ids: new Set() });
      }
    } catch (error) {
      toast.error("Failed to parse the clipboard!")
    }        
  };      
    
  const handleRowUpdate = (newRow) => {
    const updatedRows = fixedBlockTimes?.map((row) =>
      row._id === newRow._id ? { ...row, ...newRow } : row
    );
    setFixedBlockTimes(updatedRows);
    return newRow;
  };

  const handleDeleteRows = useCallback(() => {
    const selectedIds = getSelectedIds(rowSelectionModel, fixedBlockTimes);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }

    setFixedBlockTimes((prev) => prev.filter((row) => !selectedIds.includes(row._id)));
    setRowSelectionModel({ type: 'include', ids: new Set() }); 

    toast.success(`${selectedIds.length} record(s) removed successfully.`);
  }, [rowSelectionModel, fixedBlockTimes]);
  
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Delete') {
        event.preventDefault(); 
        handleDeleteRows();
      }
    }, [handleDeleteRows]
  );
  
  const handleUpdateValidUntil = useCallback(async () => {    
    try {
      const selectedIds = getSelectedIds(rowSelectionModel, fixedBlockTimes);    
      if (selectedIds.length === 0) {
        toast.error('No rows selected for updating valid until!');
        return;
      }
            
      const url = '/fixedblocktimes/updatevaliduntil';
      const payload = {
        ids: selectedIds,
        untilDate: untilDate ? parseUTCDate(untilDate) : null,
      };
            
      const res = await api.post(url, payload);

      if (res.data.success) {
        const newValidUntil = untilDate ? parseUTCDate(untilDate) : null;
        setFixedBlockTimes(prev =>
          prev.map(row =>
            selectedIds.includes(row._id)
              ? { ...row, validUntil: newValidUntil }
              : row
          )
        );
        toast.success(res.data?.message || "Fixed block times saved successfully.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save fixed block times!");
    }
  }, [rowSelectionModel, fixedBlockTimes, untilDate]);

  const handleSave = useCallback(async () => {
    if (fixedBlockTimes?.length === 0) {
      toast.error("No data to save!");
      return;
    }
    
    try {
      const url = '/fixedblocktimes/save';
      const payload = fixedBlockTimes?.map(row => {
        return ({
          sector: row.sector,
          acTypes: row.acTypes,
          blockTime: row.blockTime,
          validSince: cleanDate(row.validSince),
          validUntil: cleanDate(row.validUntil), 
        })
      });
            
      const res = await api.post(url, payload);
      if (res.data.success) {
        toast.success(res.data?.message || "Fixed block times saved successfully.");
        setFixedBlockTimes([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save fixed block times!");
    } 
  }, [fixedBlockTimes]);   

  const generateFixedBlockTimes = useCallback(async () => {
    try {
      const url = '/fixedblocktimes/generatefixedblocktimes';
      const payload = { startDate, endDate, selectedACTypes };
      
      const res = await api.post(url, payload);

      if (res.data) {
        setFixedBlockTimes(res.data)
        toast.success(res.data.message || "Fixed block times updated successfully.");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update fixed block times!");
    } 
  }, [startDate, endDate, selectedACTypes]); 

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, fixedBlockTimes);    
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (deleteType === 'single') {
        if (!idToDelete) return;
        
        await api.delete(`/fixedblocktimes/delete/${idToDelete}`);

        setFixedBlockTimes(fixedBlockTimes?.filter((item) => item._id !== idToDelete));
        toast.success('Fixed block time deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, fixedBlockTimes);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }

        const payload = { ids: selectedIds };
        const url = '/fixedblocktimes/bulk-soft-delete';        
        const res = await api.patch(url, payload);
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        
        setFixedBlockTimes(prev => prev.filter(item => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err?.message || "Failed to delete!");
    }
  }, [
    deleteType, 
    idToDelete, 
    rowSelectionModel, 
    fixedBlockTimes,
  ]);
   
  const columns = useMemo(() => [    
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      ...isMobile ? { width: 50 } : { flex: 1 },
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },    
    {
      field: "sector",
      headerName: "Sector",
      editable: true,
      ...isMobile ? { width: 90 } : { flex: 1.5 },
    }, 
    {
      field: "acTypes",
      headerName: "ACTypes",
      editable: true,
      ...isMobile ? { width: 90 } : { flex: 1.5 },
    }, 
    {
      field: "blockTime",
      headerName: "BlockTime",
      editable: true,
      align: "right",
      ...isMobile ? { width: 90 } : { flex: 1.5 },
    },
    {
      field: "validSince",
      headerName: "ValidSince",
      editable: true,
      ...isMobile ? { width: 120 } : { flex: 2 },      
      valueFormatter: (params) => {
        if (!params) return "";
        const dateObj = new Date(params);
        if (!isValid(dateObj)) return "";
        return format(dateObj, "yyyy-MM-dd");
      },
    },
    {
      field: "validUntil",
      headerName: "ValidUntil",
      editable: true,
      ...isMobile ? { width: 120 } : { flex: 2 },
      valueFormatter: (params) => {
        if (!params) return "";
        const dateObj = new Date(params);
        if (!isValid(dateObj)) return "";
        return format(dateObj, "yyyy-MM-dd");
      },
    },    
  ], [isMobile]);

  const actionColumn = useMemo(() => {    
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,      
        sortable: false,
        width: baseWidth,      
        renderCell: (params) => {        
          return (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={1}
              sx={{ width: '100%', height: '100%' }}
            >
              {canEdit && (
                <Tooltip title="Edit">
                  <IconButton
                    aria-label="Edit"
                    sx={{
                      color: theme.palette.primary.dark,
                      transition: 'color 0.3s ease',
                      '&:hover': { color: theme.palette.green.deep },
                    }}
                    onClick={() => navigate(`/fixedblocktimes/${params.row._id}`)}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Permanently delete">
                  <IconButton
                    aria-label="Delete"
                    sx={{
                      color: theme.palette.primary.dark,
                      transition: 'color 0.3s ease',
                      '&:hover': { color: theme.palette.green.deep },
                    }}
                    onClick={() => handleDeleteClick(params.row._id)}
                  >
                    <DeleteForever />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ];
  }, [
    navigate, 
    theme.palette, 
    canEdit,
    canDelete,
  ]);


  return (
    <LocalizationProvider 
      dateAdapter={AdapterDateFns}
      adapterLocale={locales['en-gb']}
    >
      <Box className="main-container-box">
        <FlexBetween>
          <Header 
            title="Fixed Block Times" 
            subtitle={`${loading ? 'Loading...' : 'Norm'}`}
          />
          {!loading && (
            <Box display="flex" alignItems="center" gap={1}>
              {!isMobile && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ position: "relative" }}>
                    <DatePicker
                      label="Until Date"
                      value={untilDate}
                      onChange={(newValue) => setUntilDate(newValue)}
                      format="dd/MM/yyyy"
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: {
                            width: 150,
                            '& .MuiInputBase-root': {
                              height: 36,
                              fontSize: '0.75rem',
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: '0.75rem',
                            },
                          },
                        },
                      }}
                    />

                    {/* Reset */}
                    {untilDate && (
                      <IconButton 
                        aria-label="clear date"
                        size="small"
                        sx={{ 
                          position: 'absolute',
                          right: 32, 
                          top: '50%',
                          transform: 'translateY(-50%)',
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                        onClick={() => setUntilDate(null)}
                      >
                        <Clear />
                      </IconButton>
                    )}
                  </Box>

                  {canEdit && (
                    <Tooltip title="Check rows to update Valid Until" arrow>
                      <span>
                        <StyledButton
                          disabled={!isAnyRowSelected(rowSelectionModel, fixedBlockTimes)}
                          onClick={handleUpdateValidUntil}
                        >
                          <Update />
                        </StyledButton>
                      </span>
                    </Tooltip>
                  )}
                  
                  <Tooltip 
                    title="Choose AC Types before generating" 
                    arrow placement="top"
                  >
                    <FormControl
                      size='small'
                      variant="outlined"
                      sx={{ minWidth: isMobile ? 90 : 120}}
                    >
                      <InputLabel>AC Types</InputLabel>
                      <Select
                        multiple
                        label="ACTypes"
                        name="ACTypes"
                        value={selectedACTypes || []}
                        onChange={(e) => setSelectedACTypes(e.target.value)}
                      >
                        {acTypes.map((item, index) => (                                    
                          <MenuItem key={index} value={item.acTypeCode}>
                            {item.acTypeCode}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Tooltip>
                              
                  <Tooltip title="Generate BT from flights" arrow>
                    <span>
                      <StyledButton 
                        onClick={generateFixedBlockTimes} 
                      >
                        <SyncAlt />
                      </StyledButton>
                    </span>              
                  </Tooltip>
                </Box>              
              )}
              
              <Tooltip title="Refresh" arrow>
                <span>
                  <StyledButton 
                    onClick={handleFixedBlockTimes} 
                  >
                    <Refresh />
                  </StyledButton>
                </span>              
              </Tooltip>

              {canEdit && (
                <Tooltip title="Save" arrow>
                  <span>
                    <StyledButton
                      disabled={fixedBlockTimes?.length === 0}
                      onClick={handleSave}
                    >
                      <Save />
                    </StyledButton>
                  </span>
                </Tooltip>
              )}

              {canDelete && (
                <Tooltip title="Bulk delete" arrow>
                  <span>
                    <StyledButton
                      disabled={!isAnyRowSelected(rowSelectionModel, fixedBlockTimes)}
                      onClick={handleBulkDelete}
                    >
                      <Delete />
                    </StyledButton>
                  </span>
                </Tooltip>
              )}

              {canCreate && (
                <Tooltip title="Create" arrow>
                  <span>
                    <StyledButton onClick={() => navigate("/fixedblocktimes/new")}>
                      <AddCircleOutline />
                    </StyledButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          )}
        </FlexBetween>
        {loading ? (
          <FlexCenter height={LAYOUT_HEIGHT}>
            <CircularProgress />
          </FlexCenter>
        ) : error ? (
          <FlexCenter height={LAYOUT_HEIGHT}>
            <Typography color="error">Error: {error?.message || ''}</Typography>
          </FlexCenter>
        ) : (
          <Box
            display="grid"
            gridTemplateColumns="repeat(12, minmax(0, 1fr))"
            gap={2}
            sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
          >
            <Box
              height={LAYOUT_HEIGHT}
              tabIndex={0}
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              sx={{ gridColumn: "span 12" }}
            >
              {isMobile ? (
                <CardList
                  rows={fixedBlockTimes}
                  primaryField="sector"
                  fields={[
                    { label: "AC Types", key: "acTypes" },
                    { label: "Block Time", key: "blockTime" },
                    { label: "Valid Since", key: "validSince" },
                    { label: "Valid Until", key: "validUntil" },
                  ]}
                  isActiveField={null}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onEdit={(id) => navigate(`/fixedblocktimes/${id}`)}
                  onDelete={handleDeleteClick}
                />
              ) : (
                <DataGridComponent
                  rows={fixedBlockTimes || []}
                  columns={columns.concat(actionColumn)}
                  columnVisibilityModel={columnVisibilityModel}
                  setColumnVisibilityModel={setColumnVisibilityModel}
                  loading={loading}
                  props={{
                    checkboxSelection: true,
                    disableRowSelectionOnClick: true,
                    processRowUpdate: handleRowUpdate,
                    onRowSelectionModelChange: setRowSelectionModel,
                    rowSelectionModel,
                    initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                  }}
                />
              )}
            </Box>
          </Box>
        )}

        <DeleteConfirm
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={executeDelete}
          deleteType={deleteType}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default FixedBlockTimes;
